import { CircularProgress, Paper } from "@mui/material";
import type { EChartsInstance } from "echarts-for-react";
import ReactECharts from "echarts-for-react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import DraggableLineControlV2 from "../components/DraggableLineControlV2";
import { useTrendingContextV2 } from "../context/TrendingContextV2";
import { useCursorData } from "../hooks/useCursorData";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import type { CustomOptions, ViewTagDataSet } from "../models/TrendingModelsV2";
import {
	type AggregatedSeries,
	type EnvelopeBand,
	aggregateSeries,
} from "../utils/aggregateSeries";
import { buildIsolatedPointsSeries } from "../utils/buildIsolatedPointsSeries";
import { buildTooltipHtml } from "../utils/buildTooltipHtml";
import {
	type ChartArea,
	type TimeRange,
	pixelToTimestamp,
	timestampToPixel,
} from "../utils/coordinateConversion";
import { findValueAtOrBefore, hasValueAt } from "../utils/findValueAtOrBefore";
import { toEChartsArrayFormat } from "../utils/formatEChartsData";
import {
	EMPTY_GAP_SET,
	findIsolatedPoints,
	insertGaps,
} from "../utils/insertGaps";
import type { AggregationMode, ZoomRange } from "../utils/trendingUrlState";

// ECharts v5 default color palette for backward compatibility
const V5_COLOR_PALETTE = [
	"#5470c6",
	"#91cc75",
	"#fac858",
	"#ee6666",
	"#73c0de",
	"#3ba272",
	"#fc8452",
	"#9a60b4",
	"#ea7ccc",
];

const DISPLAY_TARGET_FALLBACK = 2000;
const DISPLAY_SETTLE_DELAY_MS = 150;

const FULLSCREEN_ICON_PATH =
	"path://M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z";
const FULLSCREEN_EXIT_ICON_PATH =
	"path://M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z";

interface TrendingChartV2Props {
	series?: ViewTagDataSet[][];
	customOptions?: CustomOptions | null;
	isLoading?: boolean;
	isRefetching?: boolean;
	onChartReady?: (chartInstance: EChartsInstance | null) => void;
	dataLoadedTrigger?: number;
	isFullscreen?: boolean;
	onToggleFullscreen?: () => void;
	initialZoom?: ZoomRange | null;
	onZoomChange?: (zoom: ZoomRange | null) => void;
	aggregationMode?: AggregationMode;
	seriesRawCounts?: Record<number, number>;
	seriesShownCounts?: Record<number, number>;
	seriesBands?: Record<number, EnvelopeBand>;
	seriesGaps?: Record<number, ReadonlySet<number>>;
}

export const TrendingChartV2: React.FC<TrendingChartV2Props> = ({
	series = [],
	customOptions,
	isLoading = false,
	isRefetching = false,
	onChartReady,
	dataLoadedTrigger = 0,
	isFullscreen = false,
	onToggleFullscreen,
	initialZoom = null,
	onZoomChange,
	aggregationMode = "avg",
	seriesRawCounts,
	seriesShownCounts,
	seriesBands,
	seriesGaps,
}) => {
	const chartRef = useRef<ReactECharts>(null);
	const onToggleFullscreenRef = useRef(onToggleFullscreen);
	useEffect(() => {
		onToggleFullscreenRef.current = onToggleFullscreen;
	}, [onToggleFullscreen]);
	const {
		state: {
			timeScopeStart,
			timeScopeEnd,
			viewTags,
			highlightedSeries,
			cursor1Time,
			cursor2Time,
			selectedCursor,
		},
		actions: {
			setTotalScope,
			updateCursor1Time,
			updateCursor2Time,
			setSelectedCursor,
			setCursorData,
			setSeriesPointCounts,
			setChartPlotWidth,
		},
	} = useTrendingContextV2();

	// Track if we're currently updating to prevent infinite loops
	const isUpdatingRef = useRef(false);
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Chart instance and area tracking for cursors
	const [chartInstance, setChartInstance] = useState<EChartsInstance | null>(
		null,
	);
	const [chartArea, setChartArea] = useState<ChartArea | null>(null);

	// Track cursor pixel positions (local state for smooth dragging)
	const [cursor1PixelX, setCursor1PixelX] = useState<number | null>(null);
	const [cursor2PixelX, setCursor2PixelX] = useState<number | null>(null);

	// Track zoom state to preserve it across option changes
	// Use percentages (0-100) to maintain zoom when data or time scope changes
	const [zoomState, setZoomState] = useState<ZoomRange | null>(initialZoom);

	// Visible time range derived from the window and the zoom applied to it
	const visibleTimeRange = useMemo<TimeRange>(() => {
		const fullRangeStart = timeScopeStart.getTime();
		const fullRangeEnd = timeScopeEnd.getTime();

		if (!zoomState) {
			return { start: fullRangeStart, end: fullRangeEnd };
		}

		const fullRange = fullRangeEnd - fullRangeStart;
		return {
			start: fullRangeStart + (fullRange * zoomState.start) / 100,
			end: fullRangeStart + (fullRange * zoomState.end) / 100,
		};
	}, [timeScopeStart, timeScopeEnd, zoomState]);

	const onZoomChangeRef = useRef(onZoomChange);
	useEffect(() => {
		onZoomChangeRef.current = onZoomChange;
	}, [onZoomChange]);

	useEffect(() => {
		onZoomChangeRef.current?.(zoomState);
	}, [zoomState]);

	// Ref to prevent unnecessary chartArea updates
	const chartAreaRef = useRef<string>("");

	// Track previous values to detect when to clear zoom state
	const prevTimeScopeRef = useRef<string>("");
	const prevCombinedViewRef = useRef<boolean | undefined>(undefined);

	// Default options
	const opts = customOptions || {
		showGridLines: true,
		smoothLines: false,
		steppedLines: false,
		showLegend: true,
		showTooltip: true,
		combinedView: true,
		showToolbox: true,
		showYAxisNames: false,
		rawData: false,
	};

	// Process real series data and separate by tag type
	const processedData = useMemo(() => {
		// Sort viewTags in DESCENDING order to match series data order
		const viewTagsArray = Object.values(viewTags)
			.filter((tag) => tag && tag.viewTag)
			.sort((a, b) => b.order - a.order);

		if (!series || series.length === 0 || viewTagsArray.length === 0) {
			return {
				analogSeries: [],
				digitalSeries: [],
				analogTags: [],
				digitalTags: [],
			};
		}

		const analogSeries: any[] = [];
		const digitalSeries: any[] = [];
		const analogTags: any[] = [];
		const digitalTags: any[] = [];

		series.forEach((tagData, index) => {
			const viewTagInfo = viewTagsArray[index];
			if (!viewTagInfo) {
				return;
			}

			const { viewTag } = viewTagInfo;
			const echartsData = toEChartsArrayFormat(
				tagData.map((point) => ({
					timestamp:
						typeof point.timestamp === "string"
							? new Date(point.timestamp).getTime()
							: point.timestamp,
					value: point.value,
				})),
			);
			const seriesConfig = {
				name: viewTag.Alias || viewTag.TagName,
				tagName: viewTag.TagName,
				data: echartsData,
				forcedPeriodMs: viewTag.ForcedPeriodMs,
				tagId: viewTag.TagId,
				countsTagId:
					viewTag.IsBitExtracted && viewTag.SourceTagId !== undefined
						? viewTag.SourceTagId
						: viewTag.TagId,
				color: viewTag.Color,
				visible: viewTag.IsVisible,
			};

			// Separate by tag type
			if (viewTag.TagType === "D") {
				digitalSeries.push(seriesConfig);
				digitalTags.push(viewTag);
			} else {
				analogSeries.push(seriesConfig);
				analogTags.push(viewTag);
			}
		});

		return { analogSeries, digitalSeries, analogTags, digitalTags };
	}, [series, viewTags, timeScopeStart, timeScopeEnd, opts.rawData]);

	const settledTimeRange = useDebouncedValue(
		visibleTimeRange,
		DISPLAY_SETTLE_DELAY_MS,
	);

	const displayTargetPoints = useMemo(() => {
		if (!chartArea) return DISPLAY_TARGET_FALLBACK;
		const width = Math.round(chartArea.right - chartArea.left);
		return width > 0 ? width : DISPLAY_TARGET_FALLBACK;
	}, [chartArea]);

	useEffect(() => {
		setChartPlotWidth(displayTargetPoints);
	}, [displayTargetPoints, setChartPlotWidth]);

	const tickValuesRef = useRef<number[]>([]);
	const [axisTimePrecision, setAxisTimePrecision] = useState<
		"minute" | "second" | "millisecond"
	>("minute");

	const onChartFinished = useCallback(() => {
		const values = tickValuesRef.current;
		let step = 0;
		for (let i = 1; i < values.length; i++) {
			step = Math.max(step, values[i] - values[i - 1]);
		}

		let next: "minute" | "second" | "millisecond" = "minute";
		if (step > 0 && step < 1000) {
			next = "millisecond";
		} else if (step > 0 && step < 60000) {
			next = "second";
		}

		setAxisTimePrecision((prev) => (prev === next ? prev : next));
	}, []);

	const displaySeries = useMemo(() => {
		const reduce = (list: any[]) =>
			list.map((s) => {
				const agg = aggregateSeries(
					s.data,
					settledTimeRange.start,
					settledTimeRange.end,
					displayTargetPoints,
					aggregationMode,
				);

				const gapAt = opts.rawData
					? undefined
					: (seriesGaps?.[s.countsTagId] ?? EMPTY_GAP_SET);

				const gapped = insertGaps(agg.data, s.forcedPeriodMs, gapAt);

				return {
					...agg,
					data: gapped,
					isolated: findIsolatedPoints(gapped),
					band: agg.band
						? {
								min: insertGaps(agg.band.min, s.forcedPeriodMs, gapAt),
								max: insertGaps(agg.band.max, s.forcedPeriodMs, gapAt),
							}
						: undefined,
				};
			});

		return {
			analog: reduce(processedData.analogSeries),
			digital: reduce(processedData.digitalSeries),
		};
	}, [
		processedData,
		settledTimeRange,
		displayTargetPoints,
		aggregationMode,
		opts.rawData,
		seriesGaps,
	]);

	const seriesPointCountsRef = useRef<string>("");
	useEffect(() => {
		const counts: Record<number, number> = {};

		processedData.analogSeries.forEach((s, idx) => {
			counts[s.tagId] = opts.rawData
				? (displaySeries.analog[idx]?.visibleCount ?? 0)
				: (seriesRawCounts?.[s.tagId] ?? 0);
		});
		processedData.digitalSeries.forEach((s, idx) => {
			counts[s.tagId] = opts.rawData
				? (displaySeries.digital[idx]?.visibleCount ?? 0)
				: (seriesRawCounts?.[s.tagId] ?? 0);
		});

		const serialized = JSON.stringify(counts);
		if (serialized !== seriesPointCountsRef.current) {
			seriesPointCountsRef.current = serialized;
			setSeriesPointCounts(counts);
		}
	}, [
		processedData,
		displaySeries,
		setSeriesPointCounts,
		opts.rawData,
		seriesRawCounts,
	]);

	const realPointsByTagId = useMemo(() => {
		const flags: Record<number, boolean> = {};

		const mark = (list: any[], display: (AggregatedSeries | undefined)[]) => {
			list.forEach((s, idx) => {
				const rawCount = seriesRawCounts?.[s.countsTagId] ?? 0;
				const shownCount = seriesShownCounts?.[s.countsTagId] ?? 0;

				const serverReturnedRealPoints =
					opts.rawData || (rawCount > 0 && shownCount >= rawCount);
				const clientKeptRealPoints = !(display[idx]?.decimated ?? false);

				flags[s.tagId] = serverReturnedRealPoints && clientKeptRealPoints;
			});
		};

		mark(processedData.analogSeries, displaySeries.analog);
		mark(processedData.digitalSeries, displaySeries.digital);

		return flags;
	}, [
		processedData,
		displaySeries,
		opts.rawData,
		seriesRawCounts,
		seriesShownCounts,
	]);

	const analogBands = useMemo(() => {
		return processedData.analogSeries.map((s, idx) => {
			if (opts.rawData) return displaySeries.analog[idx]?.band;
			if (aggregationMode !== "envelope") return undefined;

			const band = seriesBands?.[s.tagId];
			if (!band) return band;

			const gapAt = seriesGaps?.[s.countsTagId] ?? EMPTY_GAP_SET;

			return {
				min: insertGaps(band.min, s.forcedPeriodMs, gapAt),
				max: insertGaps(band.max, s.forcedPeriodMs, gapAt),
			};
		});
	}, [
		processedData,
		displaySeries,
		opts.rawData,
		aggregationMode,
		seriesBands,
		seriesGaps,
	]);

	const symbolsVisible = useCallback(
		(
			tagId: number,
			entry: AggregatedSeries | undefined,
			fullLength: number,
		) => {
			if (!realPointsByTagId[tagId]) return false;

			const count = entry ? entry.data.length : fullLength;
			return count > 0;
		},
		[realPointsByTagId],
	);

	const getTagName = useCallback((tag: any) => tag.Alias || tag.TagName, []);

	const getActiveAxisTag = useCallback(
		(tags: any[]) => {
			if (tags.length === 0) return null;

			if (highlightedSeries) {
				const highlightedTag = tags.find(
					(tag) => tag.IsVisible && getTagName(tag) === highlightedSeries,
				);

				if (highlightedTag) return highlightedTag;
			}

			return tags.find((tag) => tag.IsVisible) || null;
		},
		[highlightedSeries, getTagName],
	);

	// Clear zoom state when time scope changes or combined view toggles
	useEffect(() => {
		const currentTimeScope = `${timeScopeStart.getTime()}-${timeScopeEnd.getTime()}`;
		const timeScopeChanged =
			currentTimeScope !== prevTimeScopeRef.current &&
			prevTimeScopeRef.current !== "";
		const combinedViewToggled =
			prevCombinedViewRef.current !== undefined &&
			opts.combinedView !== prevCombinedViewRef.current;

		if (timeScopeChanged) {
			setZoomState(null);
			setCursor1PixelX(null);
			setCursor2PixelX(null);
		} else if (combinedViewToggled) {
			setZoomState(null);
		}

		// Update refs
		prevTimeScopeRef.current = currentTimeScope;
		prevCombinedViewRef.current = opts.combinedView;
	}, [timeScopeStart, timeScopeEnd, opts.combinedView]);

	// Helper to build yAxis config with scale settings
	const buildYAxisConfig = useCallback(
		(
			tag: any,
			gridIndex: number,
			position: "left" | "right",
			offset: number = 0,
			isAxisVisible = true,
		) => {
			const baseConfig: any = {
				gridIndex,
				type: "value",
				show: isAxisVisible,
				name: isAxisVisible && opts.showYAxisNames ? getTagName(tag) : "",
				nameLocation: "middle",
				nameGap: 45,
				position,
				offset,
				splitNumber: 8, // Suggest 8 divisions, ECharts will adjust for readability
				nameTextStyle: {
					color: tag.Color,
					fontSize: 11,
					fontWeight: "bold",
				},
				axisLine: {
					show: isAxisVisible,
					lineStyle: {
						color: tag.Color,
						width: 2,
					},
				},
				axisTick: {
					show: isAxisVisible,
				},
				axisLabel: {
					show: isAxisVisible,
					color: tag.Color,
					fontSize: 10,
				},
				splitLine: {
					show:
						isAxisVisible && position === "left" && offset === 0
							? opts.showGridLines
							: false,
					lineStyle: {
						color: "#e0e0e0",
						type: "solid",
					},
				},
			};

			// Apply scale settings
			if (tag.IsAutoScale) {
				// Use automatic scaling based on data
				baseConfig.scale = true;
				// Explicitly clear min/max to ensure auto-scaling works when switching from fixed scale
				baseConfig.min = null;
				baseConfig.max = null;
			} else {
				// Use explicit min/max values
				if (tag.MinScale !== null && tag.MinScale !== undefined) {
					baseConfig.min = tag.MinScale;
				}
				if (tag.MaxScale !== null && tag.MaxScale !== undefined) {
					baseConfig.max = tag.MaxScale;
				}
			}

			// Special handling for digital tags (force 0-1 range if auto scale)
			if (tag.TagType === "D" && tag.IsAutoScale) {
				baseConfig.min = 0;
				baseConfig.max = 1;
				baseConfig.interval = 0.5;
				delete baseConfig.scale;
			}

			return baseConfig;
		},
		[opts.showGridLines, opts.showYAxisNames, getTagName],
	);

	// Determine if we should use separate grids (analog/digital)
	const useSeparateGrids =
		!opts.combinedView &&
		processedData.analogTags.length > 0 &&
		processedData.digitalTags.length > 0;

	// Generate a key that changes when grid structure changes or series count changes
	// This forces React to remount the entire chart component, avoiding merge conflicts
	// Including series count ensures chart remounts when tags are added/removed
	const chartKey = useMemo(() => {
		const seriesCount =
			processedData.analogSeries.length + processedData.digitalSeries.length;
		const mode = opts.rawData ? "raw" : aggregationMode;
		return `chart-${useSeparateGrids ? "separate" : "combined"}-${seriesCount}-${mode}`;
	}, [
		useSeparateGrids,
		processedData.analogSeries.length,
		processedData.digitalSeries.length,
		opts.rawData,
		aggregationMode,
	]);

	// Calculate cursor data (Y1, Y2, etc.)
	const cursorData = useCursorData(cursor1Time, cursor2Time, series, viewTags);

	// Update context with cursor data (use ref to prevent infinite loop)
	const cursorDataRef = useRef<string>("");
	useEffect(() => {
		const serialized = JSON.stringify(cursorData);
		if (serialized !== cursorDataRef.current) {
			cursorDataRef.current = serialized;
			setCursorData(cursorData);
		}
	}, [cursorData, setCursorData]);

	// Refs to track previous values for detecting what changed
	const prevVisibleTimeRangeRef = useRef<string>("");
	const prevCursor1TimeRef = useRef<number>(cursor1Time);
	const prevCursor2TimeRef = useRef<number>(cursor2Time);
	const isUserDraggingRef = useRef<boolean>(false);

	// Initialize cursor pixel positions when chartArea becomes available
	useEffect(() => {
		if (!chartArea) return;

		// Initialize on first render (when pixel positions are null)
		if (cursor1PixelX === null || cursor2PixelX === null) {
			const pixel1 = timestampToPixel(cursor1Time, chartArea, visibleTimeRange);
			const pixel2 = timestampToPixel(cursor2Time, chartArea, visibleTimeRange);
			setCursor1PixelX(pixel1);
			setCursor2PixelX(pixel2);
			prevVisibleTimeRangeRef.current = JSON.stringify(visibleTimeRange);
			prevCursor1TimeRef.current = cursor1Time;
			prevCursor2TimeRef.current = cursor2Time;
			return;
		}

		// Don't update anything if user is dragging
		if (isUserDraggingRef.current) return;

		const visibleRangeChanged =
			JSON.stringify(visibleTimeRange) !== prevVisibleTimeRangeRef.current;
		const cursor1TimeChanged = cursor1Time !== prevCursor1TimeRef.current;
		const cursor2TimeChanged = cursor2Time !== prevCursor2TimeRef.current;

		if (visibleRangeChanged) {
			// Visible range changed (zoom/pan): keep pixels same, update timestamps
			if (cursor1PixelX !== null && cursor2PixelX !== null) {
				const newTimestamp1 = pixelToTimestamp(
					cursor1PixelX,
					chartArea,
					visibleTimeRange,
				);
				const newTimestamp2 = pixelToTimestamp(
					cursor2PixelX,
					chartArea,
					visibleTimeRange,
				);

				updateCursor1Time(newTimestamp1);
				updateCursor2Time(newTimestamp2);

				prevVisibleTimeRangeRef.current = JSON.stringify(visibleTimeRange);
				prevCursor1TimeRef.current = newTimestamp1;
				prevCursor2TimeRef.current = newTimestamp2;
			}
		} else {
			// Timestamps changed externally (not from zoom/pan): update pixels
			if (cursor1TimeChanged) {
				const newPixel1 = timestampToPixel(
					cursor1Time,
					chartArea,
					visibleTimeRange,
				);
				setCursor1PixelX(newPixel1);
				prevCursor1TimeRef.current = cursor1Time;
			}
			if (cursor2TimeChanged) {
				const newPixel2 = timestampToPixel(
					cursor2Time,
					chartArea,
					visibleTimeRange,
				);
				setCursor2PixelX(newPixel2);
				prevCursor2TimeRef.current = cursor2Time;
			}
		}
	}, [
		chartArea,
		visibleTimeRange,
		cursor1Time,
		cursor2Time,
		cursor1PixelX,
		cursor2PixelX,
		updateCursor1Time,
		updateCursor2Time,
	]);

	// Handle cursor drag during movement: real-time updates for immediate feedback
	const handleCursor1Change = useCallback(
		(newPixelX: number) => {
			if (!chartArea || !visibleTimeRange) return;

			// Mark that user is dragging
			isUserDraggingRef.current = true;

			// Convert pixel to timestamp based on current visible range
			const newTimestamp = pixelToTimestamp(
				newPixelX,
				chartArea,
				visibleTimeRange,
			);

			// Update context with new timestamp (real-time during drag)
			updateCursor1Time(newTimestamp);

			// Update local pixel position for smooth rendering
			setCursor1PixelX(newPixelX);

			// Update refs to prevent effect from interfering
			prevCursor1TimeRef.current = newTimestamp;
		},
		[chartArea, visibleTimeRange, updateCursor1Time],
	);

	const handleCursor2Change = useCallback(
		(newPixelX: number) => {
			if (!chartArea || !visibleTimeRange) return;

			// Mark that user is dragging
			isUserDraggingRef.current = true;

			// Convert pixel to timestamp based on current visible range
			const newTimestamp = pixelToTimestamp(
				newPixelX,
				chartArea,
				visibleTimeRange,
			);

			// Update context with new timestamp (real-time during drag)
			updateCursor2Time(newTimestamp);

			// Update local pixel position for smooth rendering
			setCursor2PixelX(newPixelX);

			// Update refs to prevent effect from interfering
			prevCursor2TimeRef.current = newTimestamp;
		},
		[chartArea, visibleTimeRange, updateCursor2Time],
	);

	// Handle cursor drag end: same as change handler (already updated during drag)
	const handleCursor1DragEnd = useCallback(
		(newPixelX: number) => {
			// Final update on drag end (onChange already handled the update)
			handleCursor1Change(newPixelX);
			// Clear dragging flag
			isUserDraggingRef.current = false;
		},
		[handleCursor1Change],
	);

	const handleCursor2DragEnd = useCallback(
		(newPixelX: number) => {
			// Final update on drag end (onChange already handled the update)
			handleCursor2Change(newPixelX);
			// Clear dragging flag
			isUserDraggingRef.current = false;
		},
		[handleCursor2Change],
	);

	const chartOptions = useMemo(() => {
		const chartLeftMargin = opts.showYAxisNames ? 96 : 72;
		const chartRightMargin = 32;

		return {
			animation: false,
			stateAnimation: { duration: 0 },
			backgroundColor: "#fff",
			color: V5_COLOR_PALETTE, // Use ECharts v5 color palette for consistency
			grid: useSeparateGrids
				? [
						{
							id: "analog",
							left: `${chartLeftMargin}px`,
							right: `${chartRightMargin}px`,
							top: opts.showLegend ? "70px" : "50px", // Increased to prevent cursor circle clipping
							bottom: "55%",
							containLabel: false,
						},
						{
							id: "digital",
							left: `${chartLeftMargin}px`,
							right: `${chartRightMargin}px`,
							top: "55%",
							bottom: 60,
							containLabel: false,
						},
					]
				: [
						{
							id: "combined",
							left: `${chartLeftMargin}px`,
							right: `${chartRightMargin}px`,
							top: opts.showLegend ? "70px" : "50px", // Increased to prevent cursor circle clipping
							bottom: 60,
							containLabel: false,
						},
					],
			xAxis: !useSeparateGrids
				? [
						{
							gridIndex: 0,
							type: "time",
							show: true,
							position: "bottom",
							min: timeScopeStart.getTime(),
							max: timeScopeEnd.getTime(),
							axisLine: {
								show: true,
								lineStyle: {
									color: "#999",
								},
							},
							axisTick: {
								show: true,
								alignWithLabel: true,
							},
							axisLabel: {
								show: true,
								color: "#333",
								fontSize: 11,
								formatter: (value: number, index: number) => {
									const date = new Date(value);
									const hours = date.getHours();
									const minutes = date.getMinutes();
									if (index === 0) {
										tickValuesRef.current = [];
									}
									tickValuesRef.current.push(value);
									const hhmm = `${hours.toString().padStart(2, "0")}:${minutes
										.toString()
										.padStart(2, "0")}`;
									const ss = date.getSeconds().toString().padStart(2, "0");
									const sss = date
										.getMilliseconds()
										.toString()
										.padStart(3, "0");
									const time =
										axisTimePrecision === "minute"
											? hhmm
											: axisTimePrecision === "second"
												? `${hhmm}:${ss}`
												: `${hhmm}:${ss}.${sss}`;
									const monthNames = [
										"Jan",
										"Feb",
										"Mar",
										"Apr",
										"May",
										"Jun",
										"Jul",
										"Aug",
										"Sep",
										"Oct",
										"Nov",
										"Dec",
									];
									const monthName = monthNames[date.getMonth()];
									const day = date.getDate();

									// First tick: always show full datetime
									if (index === 0) {
										return `${monthName} ${day}, ${date.getFullYear()}\n${time}`;
									}

									// If crossing midnight (00:00), show date + time
									if (hours === 0 && minutes === 0) {
										return `${monthName} ${day}\n${time}`;
									}

									// Otherwise just show time
									return time;
								},
							},
							splitLine: {
								show: opts.showGridLines,
								lineStyle: {
									color: "#e0e0e0",
									type: "solid",
								},
							},
						},
					]
				: [
						{
							gridIndex: 0,
							type: "time",
							show: true,
							position: "top",
							min: timeScopeStart.getTime(),
							max: timeScopeEnd.getTime(),
							axisLine: {
								show: true,
								lineStyle: {
									color: "#999",
								},
							},
							axisTick: {
								show: true,
								alignWithLabel: true,
							},
							axisLabel: {
								show: true,
								color: "#333",
								fontSize: 11,
								formatter: (value: number, index: number) => {
									const date = new Date(value);
									const hours = date.getHours();
									const minutes = date.getMinutes();
									if (index === 0) {
										tickValuesRef.current = [];
									}
									tickValuesRef.current.push(value);
									const hhmm = `${hours.toString().padStart(2, "0")}:${minutes
										.toString()
										.padStart(2, "0")}`;
									const ss = date.getSeconds().toString().padStart(2, "0");
									const sss = date
										.getMilliseconds()
										.toString()
										.padStart(3, "0");
									const time =
										axisTimePrecision === "minute"
											? hhmm
											: axisTimePrecision === "second"
												? `${hhmm}:${ss}`
												: `${hhmm}:${ss}.${sss}`;
									const monthNames = [
										"Jan",
										"Feb",
										"Mar",
										"Apr",
										"May",
										"Jun",
										"Jul",
										"Aug",
										"Sep",
										"Oct",
										"Nov",
										"Dec",
									];
									const monthName = monthNames[date.getMonth()];
									const day = date.getDate();

									// First tick: always show full datetime
									if (index === 0) {
										return `${monthName} ${day}, ${date.getFullYear()}\n${time}`;
									}

									// If crossing midnight (00:00), show date + time
									if (hours === 0 && minutes === 0) {
										return `${monthName} ${day}\n${time}`;
									}

									// Otherwise just show time
									return time;
								},
							},
							splitLine: {
								show: opts.showGridLines,
								lineStyle: {
									color: "#e0e0e0",
									type: "solid",
								},
							},
						},
						{
							gridIndex: 1,
							type: "time",
							show: true,
							position: "bottom",
							min: timeScopeStart.getTime(),
							max: timeScopeEnd.getTime(),
							axisLine: {
								show: true,
								lineStyle: {
									color: "#999",
								},
							},
							axisTick: {
								show: true,
								alignWithLabel: true,
							},
							axisLabel: {
								show: true,
								color: "#333",
								fontSize: 11,
								formatter: (value: number, index: number) => {
									const date = new Date(value);
									const hours = date.getHours();
									const minutes = date.getMinutes();
									if (index === 0) {
										tickValuesRef.current = [];
									}
									tickValuesRef.current.push(value);
									const hhmm = `${hours.toString().padStart(2, "0")}:${minutes
										.toString()
										.padStart(2, "0")}`;
									const ss = date.getSeconds().toString().padStart(2, "0");
									const sss = date
										.getMilliseconds()
										.toString()
										.padStart(3, "0");
									const time =
										axisTimePrecision === "minute"
											? hhmm
											: axisTimePrecision === "second"
												? `${hhmm}:${ss}`
												: `${hhmm}:${ss}.${sss}`;
									const monthNames = [
										"Jan",
										"Feb",
										"Mar",
										"Apr",
										"May",
										"Jun",
										"Jul",
										"Aug",
										"Sep",
										"Oct",
										"Nov",
										"Dec",
									];
									const monthName = monthNames[date.getMonth()];
									const day = date.getDate();

									// First tick: always show full datetime
									if (index === 0) {
										return `${monthName} ${day}, ${date.getFullYear()}\n${time}`;
									}

									// If crossing midnight (00:00), show date + time
									if (hours === 0 && minutes === 0) {
										return `${monthName} ${day}\n${time}`;
									}

									// Otherwise just show time
									return time;
								},
							},
							splitLine: {
								show: opts.showGridLines,
								lineStyle: {
									color: "#e0e0e0",
									type: "solid",
								},
							},
						},
					],
			yAxis: (() => {
				const yAxisArray: any[] = [];

				if (useSeparateGrids) {
					const activeAnalogTag = getActiveAxisTag(processedData.analogTags);
					const activeDigitalTag = getActiveAxisTag(processedData.digitalTags);

					processedData.analogTags.forEach((tag: any, idx: number) => {
						yAxisArray.push(
							buildYAxisConfig(tag, 0, "left", 0, tag === activeAnalogTag),
						);
					});

					processedData.digitalTags.forEach((tag: any) => {
						yAxisArray.push(
							buildYAxisConfig(tag, 1, "left", 0, tag === activeDigitalTag),
						);
					});
				} else {
					const activeCombinedTag = getActiveAxisTag([
						...processedData.analogTags,
						...processedData.digitalTags,
					]);

					processedData.analogTags.forEach((tag: any) => {
						yAxisArray.push(
							buildYAxisConfig(tag, 0, "left", 0, tag === activeCombinedTag),
						);
					});

					processedData.digitalTags.forEach((tag: any) => {
						yAxisArray.push(
							buildYAxisConfig(tag, 0, "left", 0, tag === activeCombinedTag),
						);
					});
				}

				// If no axes were created at all, add a default one to prevent errors
				if (yAxisArray.length === 0) {
					yAxisArray.push({
						gridIndex: 0,
						type: "value",
						show: false,
						position: "left",
						axisLine: { show: false },
						axisTick: { show: false },
						axisLabel: { show: false },
						splitLine: { show: false },
					});
				}

				return yAxisArray;
			})(),
			series: (() => {
				// In separate view, digital axes start after analog axes
				// In combined view (or single type), digital axes also start after analog axes
				const digitalStartIdx = processedData.analogTags.length;

				return [
					// Analog series - each maps to its corresponding yAxis
					...processedData.analogSeries.map((s, idx) => ({
						name: s.name,
						type: "line",
						xAxisIndex: 0,
						yAxisIndex: idx, // Map to corresponding analog yAxis
						data: displaySeries.analog[idx]?.data ?? s.data,
						smooth: opts.steppedLines ? false : opts.smoothLines,
						step: opts.steppedLines ? "end" : undefined,
						symbol: symbolsVisible(
							s.tagId,
							displaySeries.analog[idx],
							s.data.length,
						)
							? "circle"
							: "none",
						symbolSize: 4,
						sampling: displaySeries.analog[idx]?.decimated ? undefined : "lttb",
						show: s.visible,
						lineStyle: {
							width: 2,
							color: s.color,
						},
						itemStyle: {
							color: s.color,
						},
						emphasis: {
							focus: "series",
							scale: 1.8,
							itemStyle: {
								borderColor: "#000",
								borderWidth: 1.5,
								shadowBlur: 4,
								shadowColor: "rgba(0, 0, 0, 0.35)",
							},
						},
						showSymbol: symbolsVisible(
							s.tagId,
							displaySeries.analog[idx],
							s.data.length,
						),
					})),
					// Digital series - each maps to its corresponding yAxis after analog axes
					...processedData.digitalSeries.map((s, idx) => ({
						name: s.name,
						type: "line",
						step: "end",
						xAxisIndex: useSeparateGrids ? 1 : 0,
						yAxisIndex: digitalStartIdx + idx, // Map to corresponding digital yAxis
						data: displaySeries.digital[idx]?.data ?? s.data,
						symbol: symbolsVisible(
							s.tagId,
							displaySeries.digital[idx],
							s.data.length,
						)
							? "circle"
							: "none",
						symbolSize: 4,
						show: s.visible,
						lineStyle: {
							width: 2,
							color: s.color,
						},
						itemStyle: {
							color: s.color,
						},
						emphasis: {
							focus: "series",
							scale: 1.8,
							itemStyle: {
								borderColor: "#000",
								borderWidth: 1.5,
								shadowBlur: 4,
								shadowColor: "rgba(0, 0, 0, 0.35)",
							},
						},
						showSymbol: symbolsVisible(
							s.tagId,
							displaySeries.digital[idx],
							s.data.length,
						),
					})),
					...buildIsolatedPointsSeries(
						processedData,
						displaySeries,
						useSeparateGrids,
						digitalStartIdx,
					),
					...processedData.analogSeries.flatMap((s, idx) => {
						const band = analogBands[idx];
						const hasBand = !!band && s.visible && band.min.length > 1;

						const line = {
							type: "line",
							xAxisIndex: 0,
							yAxisIndex: idx,
							symbol: "none",
							silent: true,
							z: 1,
							lineStyle: { width: 1, color: s.color, opacity: 0.55 },
						};

						return [
							{
								type: "custom",
								name: `${s.name} band`,
								xAxisIndex: 0,
								yAxisIndex: idx,
								silent: true,
								clip: true,
								z: 0,
								data: hasBand && band ? [band.min[0]] : [],
								renderItem: (_params: any, api: any) => {
									if (!hasBand || !band) return null;

									const children: any[] = [];
									let top: number[][] = [];
									let bottom: number[][] = [];

									const flush = () => {
										if (top.length > 1) {
											children.push({
												type: "polygon",
												shape: {
													points: [...top, ...bottom.slice().reverse()],
												},
												style: { fill: s.color, opacity: 0.18 },
												silent: true,
											});
										}
										top = [];
										bottom = [];
									};

									band.min.forEach((point, i) => {
										const low = point[1];
										const high = band.max[i]?.[1];
										if (low == null || high == null) {
											flush();
											return;
										}

										top.push(api.coord([point[0], high]));
										bottom.push(api.coord([point[0], low]));
									});
									flush();

									return { type: "group", children };
								},
							},
							{
								...line,
								name: `${s.name} min`,
								data: hasBand && band ? band.min : [],
							},
							{
								...line,
								name: `${s.name} max`,
								data: hasBand && band ? band.max : [],
							},
						];
					}),
				];
			})(),
			dataZoom: [
				{
					type: "inside",
					xAxisIndex: useSeparateGrids ? [0, 1] : [0],
					filterMode: "none" as const,
					zoomOnMouseWheel: false, // Disabled - using custom wheel handler for correct anchor point
					moveOnMouseMove: true,
					moveOnMouseWheel: false,
					preventDefaultMouseMove: false,
					start: zoomState ? zoomState.start : 0,
					end: zoomState ? zoomState.end : 100,
				},
				...(true
					? [
							{
								type: "slider" as const,
								xAxisIndex: useSeparateGrids ? [0, 1] : [0],
								bottom: 3,
								height: 19,
								handleSize: "80%",
								showDetail: true,
								filterMode: "none" as const, // Don't filter data, just zoom
								start: zoomState ? zoomState.start : 0,
								end: zoomState ? zoomState.end : 100,
							},
						]
					: []),
			],
			tooltip: opts.showTooltip
				? {
						trigger: "axis",
						axisPointer: {
							type: "cross",
							crossStyle: {
								color: "#999",
							},
						},
						backgroundColor: "rgba(255, 255, 255, 0.95)",
						borderColor: "#ccc",
						borderWidth: 1,
						textStyle: {
							color: "#333",
							fontSize: 12,
						},
						formatter: (params: any) => {
							if (!params || params.length === 0) return "";

							const hoveredTime = params[0].axisValue ?? params[0].value[0];
							const hoveredAxisIndex = params[0].axisIndex ?? 0;

							const seriesForHoveredGrid = useSeparateGrids
								? hoveredAxisIndex === 1
									? processedData.digitalSeries
									: processedData.analogSeries
								: [
										...processedData.analogSeries,
										...processedData.digitalSeries,
									];

							return buildTooltipHtml(
								hoveredTime,
								seriesForHoveredGrid
									.filter((s: any) => s.visible)
									.map((s: any) => ({
										name: s.name,
										tagName: s.tagName,
										color: s.color,
										value: findValueAtOrBefore(s.data, hoveredTime),
										exact:
											realPointsByTagId[s.tagId] &&
											hasValueAt(s.data, hoveredTime),
									})),
							);
						},
					}
				: undefined,
			legend: {
				show: opts.showLegend,
				top: 5,
				left: "center",
				data: [
					...processedData.analogSeries.map((s) => s.name),
					...processedData.digitalSeries.map((s) => s.name),
				],
				textStyle: {
					color: "#333",
					fontSize: 12,
				},
				// Control series visibility based on IsVisible flag
				selected: (() => {
					const selected: Record<string, boolean> = {};
					processedData.analogSeries.forEach((s) => {
						selected[s.name] = s.visible;
					});
					processedData.digitalSeries.forEach((s) => {
						selected[s.name] = s.visible;
					});
					return selected;
				})(),
			},
			toolbox:
				(opts.showToolbox || isFullscreen) &&
				(processedData.analogSeries.length > 0 ||
					processedData.digitalSeries.length > 0)
					? {
							show: true,
							left: 15,
							bottom: 5,
							padding: 0,
							feature: {
								saveAsImage: {},
								...(onToggleFullscreen
									? {
											myFullscreen: {
												show: true,
												title: isFullscreen ? "Exit Fullscreen" : "Fullscreen",
												icon: isFullscreen
													? FULLSCREEN_EXIT_ICON_PATH
													: FULLSCREEN_ICON_PATH,
												onclick: () => onToggleFullscreenRef.current?.(),
											},
										}
									: {}),
								dataZoom: {
									show: true,
									title: {
										zoom: "Area Zoom",
										back: "Restore Area Zoom",
									},
									yAxisIndex: "none",
									xAxisIndex: useSeparateGrids ? [0, 1] : [0],
									icon: {
										zoom: "path://", // Empty path to hide icon
										back: "path://", // Empty path to hide icon
									},
									iconStyle: {
										opacity: 0, // Make invisible
										borderWidth: 0,
									},
								},
								restore: {
									show: true,
									title: "Restore",
									icon: "path://", // Empty path to hide icon
									iconStyle: {
										opacity: 0, // Make invisible
										borderWidth: 0,
									},
								},
							},
						}
					: undefined,
		};
	}, [
		opts,
		processedData,
		displaySeries,
		analogBands,
		realPointsByTagId,
		symbolsVisible,
		timeScopeStart,
		timeScopeEnd,
		buildYAxisConfig,
		getActiveAxisTag,
		zoomState,
		useSeparateGrids,
		isFullscreen,
		onToggleFullscreen,
		axisTimePrecision,
	]);

	// Handle dataZoom events (zoom/pan) with debouncing
	const onDataZoom = useCallback((params: any) => {
		if (isUpdatingRef.current) return;

		const echartsInstance = chartRef.current?.getEchartsInstance();
		if (!echartsInstance) return;

		const zoom = params.batch?.[0] ?? params;
		let zoomStartPercent = zoom?.start;
		let zoomEndPercent = zoom?.end;

		if (
			typeof zoomStartPercent !== "number" ||
			typeof zoomEndPercent !== "number"
		) {
			const dataZoomOption = (
				echartsInstance.getOption().dataZoom as any[]
			)?.[0];
			zoomStartPercent = dataZoomOption?.start;
			zoomEndPercent = dataZoomOption?.end;
		}

		if (
			typeof zoomStartPercent !== "number" ||
			typeof zoomEndPercent !== "number"
		) {
			return;
		}

		// If showing full range (0-100% or very close), clear zoom state
		// This handles the "Restore" button case
		const isFullRange = zoomStartPercent <= 0.1 && zoomEndPercent >= 99.9;
		setZoomState(
			isFullRange ? null : { start: zoomStartPercent, end: zoomEndPercent },
		);
	}, []);

	// ECharts event handlers
	const onEvents = useMemo(
		() => ({
			dataZoom: onDataZoom,
			finished: onChartFinished,
		}),
		[onDataZoom, onChartFinished],
	);

	// Handle series highlighting from table row hover
	useEffect(() => {
		const echartsInstance = chartRef.current?.getEchartsInstance();
		if (!echartsInstance) return;

		if (highlightedSeries) {
			// Highlight the specified series
			echartsInstance.dispatchAction({
				type: "highlight",
				seriesName: highlightedSeries,
			});
		} else {
			// Downplay all series (remove highlight)
			echartsInstance.dispatchAction({
				type: "downplay",
			});
		}
	}, [highlightedSeries]);

	// Expose chart instance to parent and capture for cursors
	// Re-capture instance whenever chart redraws (data loads or options change)
	useEffect(() => {
		const instance = chartRef.current?.getEchartsInstance() || null;
		setChartInstance(instance);

		if (onChartReady) {
			onChartReady(instance);
		}
	}, [onChartReady, dataLoadedTrigger, chartOptions]);

	// Track chart area coordinates for cursor positioning
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const instance = chartRef.current?.getEchartsInstance();
		if (!instance || !containerRef.current) return;

		const updateChartArea = () => {
			try {
				const option = instance.getOption();
				const grid = option.grid as any[];

				if (grid && grid.length > 0) {
					const chartWidth = instance.getWidth();
					const chartHeight = instance.getHeight();

					// Calculate actual pixel positions from percentages/values/pixel strings
					const calculatePosition = (value: any, total: number) => {
						if (typeof value === "string") {
							if (value.includes("%")) {
								return (parseFloat(value) / 100) * total;
							}
							if (value.includes("px")) {
								return parseFloat(value);
							}
						}
						return typeof value === "number" ? value : 0;
					};

					// If we have multiple grids (separate analog/digital), span from top of first to bottom of last
					const firstGrid = grid[0];
					const lastGrid = grid.length > 1 ? grid[grid.length - 1] : grid[0];

					const newChartArea = {
						top: calculatePosition(firstGrid.top || 60, chartHeight),
						bottom:
							chartHeight -
							calculatePosition(lastGrid.bottom || 80, chartHeight),
						left: calculatePosition(firstGrid.left || 60, chartWidth),
						right:
							chartWidth - calculatePosition(firstGrid.right || 60, chartWidth),
					};

					// Only update if values changed (prevent infinite loop)
					const serialized = JSON.stringify(newChartArea);
					if (serialized !== chartAreaRef.current) {
						chartAreaRef.current = serialized;
						setChartArea(newChartArea);
					}
				}
			} catch (error) {
				// Silently handle chart area update failure
			}
		};

		// Create ResizeObserver to handle container resizing (panel resize)
		const resizeObserver = new ResizeObserver(() => {
			instance.resize();
			// Small delay to ensure ECharts has updated its internal state
			requestAnimationFrame(updateChartArea);
		});

		resizeObserver.observe(containerRef.current);

		// Update immediately
		updateChartArea();

		return () => {
			resizeObserver.disconnect();
		};
	}, [chartInstance, useSeparateGrids]);

	// Keyboard control for cursor navigation
	useEffect(() => {
		if (!selectedCursor) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			const range = timeScopeEnd.getTime() - timeScopeStart.getTime();
			const smallStep = range * 0.01;
			const largeStep = range * 0.05;
			const step = e.shiftKey ? largeStep : smallStep;

			switch (e.key) {
				case "ArrowLeft":
					e.preventDefault();
					if (selectedCursor === "cursor1") {
						updateCursor1Time(cursor1Time - step);
					} else {
						updateCursor2Time(cursor2Time - step);
					}
					break;

				case "ArrowRight":
					e.preventDefault();
					if (selectedCursor === "cursor1") {
						updateCursor1Time(cursor1Time + step);
					} else {
						updateCursor2Time(cursor2Time + step);
					}
					break;

				case "Escape":
					e.preventDefault();
					setSelectedCursor(null);
					break;

				case "Tab":
					e.preventDefault();
					setSelectedCursor(
						selectedCursor === "cursor1" ? "cursor2" : "cursor1",
					);
					break;
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [
		selectedCursor,
		cursor1Time,
		cursor2Time,
		timeScopeStart,
		timeScopeEnd,
		updateCursor1Time,
		updateCursor2Time,
		setSelectedCursor,
	]);

	// Custom wheel zoom handler to correctly anchor zoom to mouse cursor position
	// ECharts' built-in zoom has a bug with multiple grids - it always uses the first grid's coordinates
	useEffect(() => {
		const echartsInstance = chartRef.current?.getEchartsInstance();
		if (!echartsInstance || !chartArea) return;

		const zr = echartsInstance.getZr();
		if (!zr) return;

		const handleWheel = (e: any) => {
			const wheelEvent = e.event as WheelEvent;
			const mouseX = e.offsetX;

			if (mouseX < chartArea.left || mouseX > chartArea.right) return;

			wheelEvent.preventDefault();
			wheelEvent.stopPropagation();

			const option = echartsInstance.getOption();
			const dataZoomArray = option.dataZoom as any[];
			if (!dataZoomArray || dataZoomArray.length === 0) return;

			const currentStart = dataZoomArray[0].start ?? 0;
			const currentEnd = dataZoomArray[0].end ?? 100;

			const fullRangeStart = timeScopeStart.getTime();
			const fullRangeEnd = timeScopeEnd.getTime();
			const fullRange = fullRangeEnd - fullRangeStart;

			const visibleStart = fullRangeStart + (fullRange * currentStart) / 100;
			const visibleEnd = fullRangeStart + (fullRange * currentEnd) / 100;
			const visibleRange = visibleEnd - visibleStart;

			const chartWidth = chartArea.right - chartArea.left;
			const mouseProgress = (mouseX - chartArea.left) / chartWidth;

			const mouseTimestamp = visibleStart + visibleRange * mouseProgress;

			const zoomFactor = wheelEvent.deltaY > 0 ? 1.2 : 1 / 1.2;

			const newVisibleRange = visibleRange * zoomFactor;

			const newVisibleStart = mouseTimestamp - newVisibleRange * mouseProgress;
			const newVisibleEnd =
				mouseTimestamp + newVisibleRange * (1 - mouseProgress);

			let newStart = ((newVisibleStart - fullRangeStart) / fullRange) * 100;
			let newEnd = ((newVisibleEnd - fullRangeStart) / fullRange) * 100;

			if (newStart < 0) {
				newEnd -= newStart;
				newStart = 0;
			}
			if (newEnd > 100) {
				newStart -= newEnd - 100;
				newEnd = 100;
			}
			newStart = Math.max(0, newStart);
			newEnd = Math.min(100, newEnd);

			echartsInstance.dispatchAction({
				type: "dataZoom",
				start: newStart,
				end: newEnd,
			});
		};

		zr.on("mousewheel", handleWheel);

		return () => {
			zr.off("mousewheel", handleWheel);
		};
	}, [chartArea, timeScopeStart, timeScopeEnd, chartInstance]);

	return (
		<Paper sx={{ height: "100%", p: 1, position: "relative" }}>
			{isLoading && (
				<div
					style={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						zIndex: 10,
					}}
				>
					<CircularProgress size="2rem" />
				</div>
			)}
			<div
				ref={containerRef}
				style={{ height: "100%", width: "100%", position: "relative" }}
			>
				<ReactECharts
					key={chartKey}
					ref={chartRef}
					option={chartOptions}
					style={{
						height: "100%",
						width: "100%",
						opacity: isLoading ? 0.3 : isRefetching ? 0.55 : 1,
						transition: "opacity 150ms ease",
					}}
					notMerge={false}
					onEvents={onEvents}
					opts={{ renderer: "canvas" }}
				/>
				{/* External draggable cursor controls */}
				{chartArea && cursor1PixelX !== null && cursor2PixelX !== null && (
					<>
						<DraggableLineControlV2
							color="red"
							isStartCursor
							chartArea={chartArea}
							initialLeft={cursor1PixelX}
							onChange={handleCursor1Change}
							onDragEnd={handleCursor1DragEnd}
							otherLineCoord={cursor2PixelX}
							minSpacing={20}
							timestamp={cursor1Time}
						/>
						<DraggableLineControlV2
							color="blue"
							isStartCursor={false}
							chartArea={chartArea}
							initialLeft={cursor2PixelX}
							onChange={handleCursor2Change}
							onDragEnd={handleCursor2DragEnd}
							otherLineCoord={cursor1PixelX}
							minSpacing={20}
							timestamp={cursor2Time}
						/>
					</>
				)}
			</div>
		</Paper>
	);
};
